import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Button, Form, Modal, FormControl, Row, Col } from "react-bootstrap";
import { get } from "lodash";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import styledConst from "styles";

import {
    getActuatorListByGatewayIds,
    getSensorListByGatewayIds,
    updateActuatorCommand,
} from "service/gatewayService";
import { getSensorTypes } from "utils/gatewayFunctions";
import { updateFormFields, getGeneralColProps } from "utils/react-bootstrap";
import { DeviceTypeDict, ACTION_FAIL_MESSAGE } from "constant";
import { displaySensorType, isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert } from "utils/alert";
import { useAllDevices } from "hooks/useDevices";
import { Device, SensorTypeObject } from "types/Gateways";
import { LightRadio } from "components/common";

const StyledContainer = styled(Modal)`
    .modal-body {
        .filter-container {
            color: ${styledConst.thirdTextColor};

            .filterBy {
                background: #f8f8f8;
                border: 1px solid #eaeaea;
                border-radius: 4px;
                margin: 10px 0px;
                align-items: center;
                padding: 10px;

                select {
                    height: fit-content;
                    padding: 5px;
                    background: white;
                    border: 1px solid #ced4da;
                    border-radius: 3px;
                }
            }
        }

        .scroll-multiple-list {
            max-height: 55vh;
        }

        .warning-message {
            color: ${styledConst.warningTextColor};
        }
        .change-to-auto {
            color: ${styledConst.Primary_Green_1};
            cursor: pointer;
            text-decoration: none;
        }
    }

    .modal-dialog {
        width: 650px;
        max-width: 650px;
    }
    .modal-option .custom-control-label:before,
    .modal-option .custom-control-label:after {
        top: 50%;
        transform: translateY(-50%);
    }
    .modal-options {
        position: relative;
        min-height: 10vh;
        max-height: 50vh;
        overflow-y: auto;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
    }
    .modal-options::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: 768px) {
        .modal-dialog {
            width: unset !important;
            max-width: unset !important;
        }
    }
`;

const serviceDict: { [key: string]: any } = {
    [DeviceTypeDict.sensor.label]: {
        title: "Select Sensor",
        // allDeviceLabel: "All Sensors",
        getListByGwId: getSensorListByGatewayIds,
        getListType: getSensorTypes,
    },
    [DeviceTypeDict.actuator.label]: {
        title: "Select Actuator",
        // allDeviceLabel: "All Actuators",
        getListByGwId: getActuatorListByGatewayIds,
        getListType: getSensorTypes,
    },
};

type SearchValues = {
    gatewayId: string;
    sensorType: string;
    search: string;
};

type SensorActuatorSelectModalProps = {
    isVisible: boolean;
    onClose: () => void;
    onFinish: (sensor: CustomDevice) => void;
    defaultSearchValue?: SearchValues;
    unitType: string;
    gatewayId?: string;
};

type CustomDevice = Device & {
    _localAuto?: boolean;
    _updating?: boolean;
};

const SensorActuatorSelectModal = (props: SensorActuatorSelectModalProps) => {
    const {
        isVisible,
        onClose,
        onFinish,
        defaultSearchValue = {
            gatewayId: "",
            sensorType: "",
            search: "",
        },
        unitType = DeviceTypeDict.sensor.label,
        gatewayId,
    } = props;

    const { title } = serviceDict[unitType];
    const [sensors, setSensors] = useState<CustomDevice[] | null>(null);
    const [filterSensors, setFilterSensors] = useState<CustomDevice[] | null>(
        null
    );
    const { allDevices, allRegistries, isLoading } = useAllDevices({
        shouldFetch: isVisible,
    });
    // Filter only onboarded registries
    const registries = (allRegistries ?? []).filter(({ info }) => info);

    const [searchValues, setSearchValues] = useState<SearchValues>({
        ...defaultSearchValue,
        gatewayId: gatewayId ?? defaultSearchValue.gatewayId,
    });
    const [sensorTypes, setSensorTypes] = useState<SensorTypeObject[]>([]);
    const [selectedSensor, setSelectedSensor] = useState<CustomDevice | null>(
        null
    );

    useEffect(() => {
        if (!allDevices) {
            return;
        }

        const validItems = allDevices.filter(
            ({ isActuator, gatewayId: _gatewayId }) => {
                const filterGatewayId =
                    gatewayId ?? searchValues.gatewayId ?? "";
                return (
                    (unitType === DeviceTypeDict.actuator.label
                        ? isActuator
                        : !isActuator) &&
                    (filterGatewayId === "" || filterGatewayId === _gatewayId)
                );
            }
        );

        setFilterSensors(validItems);
        setSensors(validItems);
        setSensorTypes(getSensorTypes(validItems));
    }, [allDevices, unitType, searchValues.gatewayId, gatewayId]);

    useEffect(() => {
        if (isVisible) {
            // Use gatewayId prop if provided, otherwise use searchValues.gatewayId
            const filterGatewayId = gatewayId ?? searchValues.gatewayId ?? "";
            const filterSensors = (sensors || []).filter(
                ({ gatewayId: _gatewayId }) =>
                    filterGatewayId === "" || filterGatewayId === _gatewayId
            );

            setFilterSensors(filterSensors);

            // Reset searchValues when modal opens
            setSearchValues({
                ...defaultSearchValue,
                gatewayId: gatewayId ?? defaultSearchValue.gatewayId,
            });

            // Update the Sensor Type list by gateway
            setSensorTypes(getSensorTypes(filterSensors));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, gatewayId]);

    useEffect(() => {
        // Use gatewayId prop if provided, otherwise use searchValues.gatewayId
        const filterGatewayId = gatewayId ?? searchValues.gatewayId ?? "";
        const { sensorType = "", search = "" } = searchValues;
        setSelectedSensor(null);

        const filterByGateway = [...(sensors || [])].filter(
            ({ gatewayId: _gatewayId }) =>
                filterGatewayId === "" || filterGatewayId === _gatewayId
        );
        const filterSensors = filterByGateway
            .filter(
                ({ sensorType: _sensorType, said, app }) =>
                    sensorType === "" ||
                    sensorType === _sensorType ||
                    sensorType === get(app, `[${said}].NAME`)
            )
            .filter(
                ({ sensorName }: { sensorName: string }) =>
                    search === "" ||
                    sensorName.toLowerCase().includes(search.toLowerCase())
            );

        setFilterSensors(filterSensors);
        setSensorTypes(getSensorTypes(filterByGateway));
    }, [searchValues, sensors, gatewayId]);

    const selectSensor = (i: CustomDevice) => {
        setSelectedSensor(i);
    };

    const handleChangeToAuto = async (
        event:
            | React.MouseEvent<HTMLSpanElement>
            | React.KeyboardEvent<HTMLSpanElement>,
        optionId: string,
        gatewayId: string,
        ldsuUuid: string,
        said: number
    ) => {
        event?.stopPropagation();

        const idMatcher = (s: CustomDevice) =>
            `${s.gatewayId}_${s.name}_${s.ldsuUID}_${s.said}` === optionId;

        // Prevent double clicks if already updating
        if ((sensors || []).some((s) => idMatcher(s) && s._updating)) return;

        // mark as updating
        setSensors((prev) =>
            (prev || []).map((s) =>
                idMatcher(s) ? { ...s, _updating: true } : s
            )
        );

        try {
            const res = await updateActuatorCommand(
                gatewayId,
                ldsuUuid,
                String(said),
                { auto: true }
            );

            if (!isHttpSuccess(res?.status)) {
                const { message } = getAPIError(res, ACTION_FAIL_MESSAGE);

                showErrorAlert({
                    message,
                });
                throw new Error(message);
            }

            // Optimistically mark the sensor in local `sensors` state as having auto enabled and clear updating
            setSensors((prev) =>
                (prev || []).map((s) =>
                    idMatcher(s)
                        ? ({
                              ...s,
                              _localAuto: true,
                              _updating: false,
                          } as CustomDevice)
                        : s
                )
            );
        } catch (err) {
            // on error, clear updating flag
            setSensors((prev) =>
                (prev || []).map((s) =>
                    idMatcher(s)
                        ? ({ ...s, _updating: false } as CustomDevice)
                        : s
                )
            );
        }
    };

    const renderActuatorModeControl = ({
        hasAuto,
        optionId,
        _gw_id,
        _ldsu_uuid,
        said,
        sensor,
    }: {
        hasAuto: boolean;
        optionId: string;
        _gw_id: string;
        _ldsu_uuid: string;
        said: number;
        sensor: CustomDevice;
    }) => {
        if (hasAuto) {
            return <div>Control: Auto (Control by Events)</div>;
        }

        return (
            <div>
                Control: Manual (Cannot control by Events)
                <Button
                    variant="link"
                    tabIndex={0}
                    className="change-to-auto btn-text"
                    onClick={(e) =>
                        handleChangeToAuto(
                            e,
                            optionId,
                            _gw_id,
                            _ldsu_uuid,
                            said
                        )
                    }
                >
                    {sensor._updating ? "..." : "Change to Auto"}
                </Button>
            </div>
        );
    };

    const renderSensorList = () => {
        if (isLoading) {
            return <div className="warning-message">Loading...</div>;
        }

        if (!allDevices?.length) {
            return (
                <div className="error-message">
                    You don't have any device connected yet. Please ensure your
                    gateway is onboarded.
                </div>
            );
        }

        return (
            <div className="scroll-multiple-list float-left">
                {(filterSensors ?? []).map((sensor, i: number) => {
                    const {
                        ldsuUID: _ldsu_uuid,
                        said,
                        name,
                        gatewayId: _gw_id,
                        isActuator,
                    } = sensor;

                    const gw: any = registries.find(
                        (r: any) => r.gateway_id === _gw_id
                    );

                    const _sensor_actuator =
                        gw.configs.find((sa: any) => sa.UID === _ldsu_uuid) ??
                        {};
                    const isAutoMode =
                        (_sensor_actuator.CMDS?.[said]?.auto as boolean) ??
                        false;

                    const optionId = `${_gw_id}_${name}_${_ldsu_uuid}_${said}`;
                    const hasAuto = isAutoMode || !!sensor._localAuto;
                    const disabled = !!isActuator && !hasAuto;

                    let selectedOptionId;
                    if (selectedSensor) {
                        selectedOptionId = `${selectedSensor.gatewayId}_${selectedSensor.name}_${selectedSensor.ldsuUID}_${selectedSensor.said}`;
                    }

                    return (
                        <div key={optionId} className="modal-option">
                            <LightRadio
                                name="selected-sensor"
                                value={optionId}
                                checked={
                                    !!(
                                        selectedSensor &&
                                        optionId === selectedOptionId
                                    )
                                }
                                custom
                                disabled={disabled}
                                onChange={(e) => {
                                    selectSensor(sensor);
                                }}
                                type="radio"
                                id={optionId}
                                label={
                                    <Box
                                        onClick={(e) => {
                                            if (disabled) return;
                                            selectSensor(sensor);
                                        }}
                                        onKeyDown={(e) => {
                                            if (disabled) return;
                                            selectSensor(sensor);
                                        }}
                                        display="flex"
                                        flexDirection="column"
                                        color={styledConst.Primary_Blue_4}
                                        gap="2px"
                                        fontSize={14}
                                    >
                                        <div
                                            style={{
                                                fontSize: 16,
                                                color: styledConst.thirdTextColor,
                                                whiteSpace: "pre-wrap",
                                            }}
                                        >
                                            {sensor.sensorName}
                                        </div>
                                        <div>{sensor.busName}</div>
                                        <div>UUID: {sensor.ldsuUID}</div>
                                        <div>{displaySensorType(sensor)}</div>
                                        {isActuator && (
                                            <div>
                                                {renderActuatorModeControl({
                                                    hasAuto,
                                                    optionId,
                                                    _gw_id,
                                                    _ldsu_uuid,
                                                    said,
                                                    sensor,
                                                })}
                                            </div>
                                        )}
                                    </Box>
                                }
                                className="float-left"
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <StyledContainer
            show={isVisible}
            onHide={onClose}
            animation={false}
            centered
        >
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
                <Button variant="" className="close-button" onClick={onClose}>
                    <span className="material-icons">close</span>
                </Button>
            </Modal.Header>
            <Modal.Body>
                <Form className="filter-container">
                    <Row className="filterBy">
                        <Col {...getGeneralColProps(2)}>Filter by:</Col>
                        <Col {...getGeneralColProps(5)}>
                            <FormControl
                                as="select"
                                aria-label="org-name"
                                name="gatewayId"
                                disabled={isLoading || !!gatewayId}
                                value={searchValues.gatewayId}
                                onChange={(e: any) =>
                                    updateFormFields(
                                        e,
                                        searchValues,
                                        setSearchValues
                                    )
                                }
                            >
                                <option value="">All Gateways</option>

                                {(registries || []).map(
                                    ({ name, gateway_id: gatewayId }: any) => {
                                        return (
                                            <option
                                                key={gatewayId}
                                                value={gatewayId}
                                            >
                                                {name}
                                            </option>
                                        );
                                    }
                                )}
                            </FormControl>
                        </Col>
                        <Col {...getGeneralColProps(5)}>
                            <FormControl
                                as="select"
                                name="sensorType"
                                placeholder="All types"
                                disabled={isLoading}
                                value={searchValues.sensorType}
                                onChange={(e: any) =>
                                    updateFormFields(
                                        e,
                                        searchValues,
                                        setSearchValues
                                    )
                                }
                            >
                                <option value="">All types</option>

                                {(sensorTypes || [])
                                    .filter(({ id, name }) => id && name)
                                    .map(({ id, name }) => {
                                        return (
                                            <option key={id} value={id}>
                                                {name}
                                            </option>
                                        );
                                    })}
                            </FormControl>
                        </Col>
                    </Row>
                    <Row>
                        <FormControl
                            type="text"
                            name="search"
                            placeholder="Search"
                            className="mb-4"
                            value={searchValues.search || ""}
                            onChange={(e) => {
                                updateFormFields(
                                    e,
                                    searchValues,
                                    setSearchValues
                                );
                            }}
                        />
                    </Row>
                </Form>

                {renderSensorList()}
            </Modal.Body>
            {selectedSensor !== null && (
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={() => onFinish(selectedSensor)}
                    >
                        DONE
                    </Button>
                </Modal.Footer>
            )}
        </StyledContainer>
    );
};

SensorActuatorSelectModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    defaultSearchValue: PropTypes.object,
};

export default SensorActuatorSelectModal;
